// /////////////////////////////////////////////////////////////////////////////////////
//                              Copyright (c) 2026
//                                    RXO Inc.
//                          All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
// /////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";

import Modal from "../../Modal";
import SystemErrorModal from "../../SystemErrorModal";
import Spinner from "../../icons/Spinner";
import { closedWonReason, closedWonReasonOptions } from "../../../helpers/types";
import messages from "../../../helpers/messages";

function SelfPricedClosedWonDetailsModal({
  open,
  isLoading,
  isError,
  onSubmit,
  onCancel,
  onRetrySubmit
}) {
  const [reason, setReason] = useState(closedWonReason.undefined);
  const [winLossComments, setWinLossComments] = useState("");
  const [validationError, setValidationError] = useState("");

  const handleSubmit = () => {
    if (reason === closedWonReason.undefined) {
      setValidationError(messages.closedWonReasonRequired.defaultMessage);
      return;
    }
    if (!winLossComments.trim()) {
      setValidationError(messages.closedWonCommentsRequired.defaultMessage);
      return;
    }
    setValidationError("");
    onSubmit({ closedWonReason: reason, winLossComments: winLossComments.trim() });
  };

  return (
    <>
      {isError && (
        <SystemErrorModal
          open={isError}
          large={true}
          closeModal={onCancel}
          handleExit={onCancel}
          handleTryAgain={onRetrySubmit}
        />
      )}
      <Modal
        title={messages.selfPricedClosedWonDetailsTitle.defaultMessage}
        open={open && !isError}
        handleClose={onCancel}
        titleClass="modal__title_light uppercase"
        closeButtonClass="modal__close-button mr-16 mt-16"
        bodyClass="border-top-grey pl-24 pr-24 pt-16 pb-32"
        large={true}
      >
        <div className="font-size-16">
          <label
            className="block univers-67-cond mb-8"
            htmlFor="self-priced-closed-won-reason"
          >
            {messages.closedWonReasonLabel.defaultMessage}
          </label>
          <select
            id="self-priced-closed-won-reason"
            data-testid="self-priced-closed-won-reason"
            className="one-whole mb-16 p-8 font-size-14 select--auto-height"
            value={reason}
            onChange={e => {
              setReason(Number(e.target.value));
              setValidationError("");
            }}
            disabled={isLoading}
          >
            <option value={closedWonReason.undefined}>
              {messages.closedWonReasonPlaceholder.defaultMessage}
            </option>
            {closedWonReasonOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <label
            className="block univers-67-cond mb-8"
            htmlFor="self-priced-win-loss-comments"
          >
            {messages.winCommentsLabel.defaultMessage}
          </label>
          <textarea
            id="self-priced-win-loss-comments"
            data-testid="self-priced-win-loss-comments"
            className="one-whole mb-8 p-8 font-size-14"
            rows={5}
            maxLength={32768}
            style={{ maxHeight: 160, overflowY: "auto" }}
            value={winLossComments}
            onChange={e => {
              setWinLossComments(e.target.value);
              setValidationError("");
            }}
            disabled={isLoading}
          />

          {validationError ? (
            <p className="text-rfq-red font-size-14 mb-8">{validationError}</p>
          ) : null}

          <div>
            {isLoading ? (
              <button
                type="button"
                className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
                data-testid="self-priced-closed-won-spinner"
              >
                <Spinner
                  className="spinner"
                  fill="#ffffff"
                  height="16px"
                  width="16px"
                />
              </button>
            ) : (
              <button
                type="button"
                className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
                data-testid="self-priced-closed-won-submit"
                onClick={handleSubmit}
              >
                {messages.selfPricedClosedWonSubmit.defaultMessage}
              </button>
            )}
            <button
              type="button"
              data-testid="self-priced-closed-won-cancel"
              onClick={onCancel}
              className="secondary-button fr univers-67-bold-cond mt-12 mb-24 mr-12 px-16 short-button"
              disabled={isLoading}
            >
              {messages.cancel.defaultMessage}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SelfPricedClosedWonDetailsModal;
